import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  ChatBubbleLeftRightIcon,
  ClockIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

import Layout from '../../components/layout/Layout';
import { WorkspaceSelector } from '../../components/common/RAGDomainSelector';
import { useRAGWorkspaces } from '../../hooks/useRAGDomains';
import {
  Badge,
  Button,
  Page,
  PageBody,
  Panel,
  PanelBody,
  PanelDescription,
  PanelHeader,
  PanelTitle,
  Spinner,
} from '../../components/ui';
import { AIConsoleShell, type AIConsoleShellSummaryItem } from '../../components/ai-console/AIConsoleShell';

const API_BASE =
  process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5001');
const TELEMETRY_POLL_MS = 10000;

interface WorkspaceTelemetryRequest {
  conversation_id: number;
  conversation_uuid: string;
  user_message_id?: number | null;
  assistant_message_id: number;
  request_text?: string | null;
  response_text: string;
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
  created_at: string;
  latency_ms?: number | null;
  trace_id?: string | null;
  trace_url?: string | null;
  total_cost_usd?: number | null;
}

interface WorkspaceLangfuseTrace {
  trace_id: string;
  name?: string | null;
  timestamp?: string | null;
  session_id?: string | null;
  user_id?: string | null;
  latency_ms?: number | null;
  total_cost_usd?: number | null;
  prompt_tokens?: number | null;
  completion_tokens?: number | null;
  total_tokens?: number | null;
  url?: string | null;
}

interface WorkspaceTelemetryResponse {
  workspace_id: number;
  workspace_name: string;
  langfuse_enabled: boolean;
  langfuse_host?: string | null;
  langfuse_public_url?: string | null;
  langfuse_embed_url?: string | null;
  langfuse_project_id?: string | null;
  recent_requests: WorkspaceTelemetryRequest[];
  traces: WorkspaceLangfuseTrace[];
  traces_fetch_error?: string | null;
}

function truncate(text: string | null | undefined, maxLength = 220): string {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength)}...`;
}

function formatTime(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
}

function formatUsd(value: number | null | undefined): string {
  if (value == null) return '—';
  if (value < 0.01) return '$0.00';
  if (value < 1) return `$${value.toFixed(4)}`;
  return `$${value.toFixed(2)}`;
}

export default function TelemetryPage() {
  const token = localStorage.getItem('auth_token');
  const [searchParams, setSearchParams] = useSearchParams();
  const [telemetry, setTelemetry] = useState<WorkspaceTelemetryResponse | null>(null);
  const [isLoadingTelemetry, setIsLoadingTelemetry] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    workspaces,
    selectedWorkspace,
    selectedWorkspaceId,
    setSelectedWorkspaceId,
    isLoading: isLoadingWorkspaces,
    error: workspacesError,
    refresh: refreshWorkspaces,
  } = useRAGWorkspaces({ autoSelect: false });

  useEffect(() => {
    const rawWorkspaceId = searchParams.get('workspaceId');
    if (!rawWorkspaceId) return;
    const parsedWorkspaceId = parseInt(rawWorkspaceId, 10);
    if (Number.isNaN(parsedWorkspaceId) || parsedWorkspaceId <= 0) return;
    if (parsedWorkspaceId !== selectedWorkspaceId) {
      setSelectedWorkspaceId(parsedWorkspaceId);
    }
  }, [searchParams, selectedWorkspaceId, setSelectedWorkspaceId]);

  useEffect(() => {
    if (selectedWorkspaceId !== null || workspaces.length === 0) return;
    setSelectedWorkspaceId(workspaces[0].id);
  }, [workspaces, selectedWorkspaceId, setSelectedWorkspaceId]);

  useEffect(() => {
    if (selectedWorkspaceId === null) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      const selectedWorkspaceText = String(selectedWorkspaceId);
      if (next.get('workspaceId') === selectedWorkspaceText) {
        return prev;
      }
      next.set('workspaceId', selectedWorkspaceText);
      return next;
    }, { replace: true });
  }, [selectedWorkspaceId, setSearchParams]);

  const fetchTelemetry = useCallback(
    async (forceLoading = false) => {
      if (!token) {
        setError('Not authenticated');
        return;
      }
      if (!selectedWorkspaceId) {
        setTelemetry(null);
        return;
      }

      if (forceLoading) {
        setIsLoadingTelemetry(true);
      }

      try {
        setError(null);
        const response = await fetch(`${API_BASE}/v1/workspaces/${selectedWorkspaceId}/telemetry?limit=30`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) {
          const errorBody = await response.text();
          throw new Error(errorBody || `Failed to fetch telemetry (${response.status})`);
        }
        const payload = (await response.json()) as WorkspaceTelemetryResponse;
        setTelemetry(payload);
      } catch (fetchError) {
        const message = fetchError instanceof Error ? fetchError.message : 'Failed to fetch telemetry';
        setError(message);
      } finally {
        if (forceLoading) {
          setIsLoadingTelemetry(false);
        }
      }
    },
    [selectedWorkspaceId, token],
  );

  useEffect(() => {
    if (!selectedWorkspaceId) {
      setTelemetry(null);
      return;
    }

    void fetchTelemetry(true);
    const interval = window.setInterval(() => {
      void fetchTelemetry(false);
    }, TELEMETRY_POLL_MS);

    return () => window.clearInterval(interval);
  }, [selectedWorkspaceId, fetchTelemetry]);

  const requests = telemetry?.recent_requests || [];
  const traces = telemetry?.traces || [];
  const externalLangfuseUrl = telemetry?.langfuse_public_url || telemetry?.langfuse_embed_url || '';
  const totalTraceCost = traces.reduce((sum, trace) => sum + (trace.total_cost_usd || 0), 0);
  const shellSummary: AIConsoleShellSummaryItem[] = [
    {
      label: 'Workspace',
      value: selectedWorkspace?.name || telemetry?.workspace_name || 'Choose one',
      detail: 'Telemetry is workspace-specific so you can connect a real user request to its exact trace trail.',
      tone: selectedWorkspace ? ('brand' as const) : ('warning' as const),
    },
    {
      label: 'Tracing status',
      value: telemetry?.langfuse_enabled ? 'Enabled' : 'Awaiting data',
      detail: telemetry?.langfuse_enabled
        ? 'Langfuse is connected for this workspace and live traces should appear below.'
        : 'Tracing is off or the workspace has not produced trace data yet.',
      tone: telemetry?.langfuse_enabled ? ('success' as const) : ('warning' as const),
    },
    {
      label: 'Recent requests',
      value: requests.length,
      detail: 'These are the latest captured assistant responses for the selected workspace.',
    },
    {
      label: 'Trace spend',
      value: formatUsd(totalTraceCost),
      detail: 'Spend is rolled up from the traces returned for the active workspace view.',
    },
  ];

  return (
    <Layout>
      <Page maxWidth="2xl">
        <PageBody>
          <AIConsoleShell
            sectionId="telemetry"
            title="Watch live workspace traffic and the traces behind it."
            description="Telemetry connects a real request, its answer, and the Langfuse trace that explains what happened. It is the best place to start when someone reports an issue from a live workspace conversation."
            plainLanguageSummary="Use this page when you need to answer, 'What did a real user ask, what did the assistant return, and where can I inspect the exact trace?' Choose a workspace first, then open a request or trace from the same screen."
            actions={
              <div className="flex flex-wrap items-center gap-2">
                <WorkspaceSelector
                  workspaces={workspaces}
                  selectedWorkspaceId={selectedWorkspaceId}
                  onSelect={setSelectedWorkspaceId}
                  isLoading={isLoadingWorkspaces}
                  error={workspacesError}
                  onRefresh={refreshWorkspaces}
                  placeholder="Select workspace"
                />
                <Button variant="outline" size="sm" onClick={() => void fetchTelemetry(true)}>
                  <ArrowPathIcon className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </div>
            }
            summaryItems={shellSummary}
          />

          {!selectedWorkspace && !isLoadingWorkspaces && (
            <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-5 text-sm text-gray-600 dark:border-dark-border dark:bg-dark-surface dark:text-gray-300">
              Select a workspace to pull in live request history and Langfuse traces.
            </div>
          )}

          {error && (
            <div className="mb-4 mt-6 rounded-2xl border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-500 dark:text-red-300">
              {error}
            </div>
          )}

          {isLoadingTelemetry && !telemetry && (
            <div className="flex items-center justify-center py-16">
              <Spinner size="lg" />
            </div>
          )}

          {telemetry && (
            <div className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-[1.2fr,0.8fr]">
              <Panel>
                <PanelHeader>
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <PanelTitle>Recent Workspace Requests</PanelTitle>
                      <PanelDescription>
                        Start with a real conversation turn, then jump directly into the linked trace or chat thread.
                      </PanelDescription>
                    </div>
                    <Badge variant="secondary">{requests.length} captured</Badge>
                  </div>
                </PanelHeader>
                <PanelBody>
                  {requests.length === 0 ? (
                    <div className="rounded-2xl border border-gray-200 bg-gray-50/80 p-4 text-sm text-gray-500 dark:border-dark-border/50 dark:bg-dark-surface-2/60 dark:text-gray-400">
                      No workspace requests have been captured yet for this selection.
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {requests.map((request) => (
                        <div
                          key={request.assistant_message_id}
                          className="rounded-2xl border border-gray-200 bg-gray-50/80 p-4 dark:border-dark-border/50 dark:bg-dark-surface-2/60"
                        >
                          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                            <div>
                              <p className="text-xs text-gray-500 dark:text-gray-400">{formatTime(request.created_at)}</p>
                              <div className="mt-2 flex flex-wrap items-center gap-2">
                                <Badge variant={request.trace_url ? 'success' : 'outline'}>
                                  {request.trace_url ? 'Linked trace' : 'No trace link'}
                                </Badge>
                                <Badge variant="outline">{request.total_tokens} tokens</Badge>
                                <Badge variant="outline">
                                  {request.latency_ms ? `${Math.round(request.latency_ms)} ms` : '— ms'}
                                </Badge>
                                <Badge variant="outline">{formatUsd(request.total_cost_usd)}</Badge>
                              </div>
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                              {request.trace_url && (
                                <a
                                  href={request.trace_url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="inline-flex items-center gap-1 text-sm font-medium text-eliza-red hover:underline"
                                >
                                  <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                                  Open trace
                                </a>
                              )}
                              <Link
                                to={`/chat/${telemetry.workspace_id}/${request.conversation_uuid}`}
                                className="inline-flex items-center gap-1 text-sm font-medium text-eliza-red hover:underline"
                              >
                                <ChatBubbleLeftRightIcon className="h-4 w-4" />
                                Open chat
                              </Link>
                            </div>
                          </div>
                          <p className="text-sm leading-6 text-charcoal dark:text-gray-100">
                            <span className="font-semibold text-gray-500 dark:text-gray-400">User asked:</span>{' '}
                            {truncate(request.request_text, 260)}
                          </p>
                          <p className="mt-2 text-sm leading-6 text-charcoal dark:text-gray-100">
                            <span className="font-semibold text-gray-500 dark:text-gray-400">Assistant answered:</span>{' '}
                            {truncate(request.response_text, 280)}
                          </p>
                          <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                            Prompt {request.prompt_tokens} / Completion {request.completion_tokens}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </PanelBody>
              </Panel>

              <div className="space-y-6">
                <Panel>
                  <PanelHeader>
                    <PanelTitle>Trace Health</PanelTitle>
                    <PanelDescription>
                      Confirm that Langfuse is connected before you start chasing a live issue.
                    </PanelDescription>
                  </PanelHeader>
                  <PanelBody className="space-y-3 text-sm">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-gray-500 dark:text-gray-400">Workspace</span>
                      <span className="font-medium text-charcoal dark:text-gray-100">{telemetry.workspace_name}</span>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-gray-500 dark:text-gray-400">Tracing</span>
                      <Badge variant={telemetry.langfuse_enabled ? 'success' : 'warning'}>
                        {telemetry.langfuse_enabled ? 'Enabled' : 'Disabled'}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-gray-500 dark:text-gray-400">Traces returned</span>
                      <span className="font-medium text-charcoal dark:text-gray-100">{traces.length}</span>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-gray-500 dark:text-gray-400">Trace spend</span>
                      <span className="font-medium text-charcoal dark:text-gray-100">{formatUsd(totalTraceCost)}</span>
                    </div>

                    {telemetry.traces_fetch_error && (
                      <div className="rounded-2xl border border-yellow-500/30 bg-yellow-500/10 p-3 text-xs text-yellow-700 dark:text-yellow-300">
                        <ExclamationTriangleIcon className="mr-1 inline h-4 w-4" />
                        {telemetry.traces_fetch_error}
                      </div>
                    )}

                    {externalLangfuseUrl && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(externalLangfuseUrl, '_blank', 'noopener,noreferrer')}
                      >
                        <ArrowTopRightOnSquareIcon className="w-4 h-4 mr-2" />
                        Open Langfuse
                      </Button>
                    )}
                  </PanelBody>
                </Panel>

                <Panel>
                  <PanelHeader>
                    <PanelTitle>Recent Langfuse Traces</PanelTitle>
                    <PanelDescription>
                      Open the freshest traces when you need timing, cost, or model details beyond the request cards.
                    </PanelDescription>
                  </PanelHeader>
                  <PanelBody>
                    <div className="max-h-[32rem] space-y-3 overflow-y-auto">
                      {traces.length === 0 ? (
                        <div className="rounded-2xl border border-gray-200 bg-gray-50/80 p-4 text-sm text-gray-500 dark:border-dark-border/50 dark:bg-dark-surface-2/60 dark:text-gray-400">
                          No Langfuse traces were returned for this workspace yet.
                        </div>
                      ) : (
                        traces.map((trace) => (
                          <div
                            key={trace.trace_id}
                            className="rounded-2xl border border-gray-200 bg-gray-50/80 p-4 dark:border-dark-border/50 dark:bg-dark-surface-2/60"
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <p className="truncate text-sm font-semibold text-charcoal dark:text-gray-100">
                                  {trace.name || 'workspace.chat'}
                                </p>
                                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                  Session: {trace.session_id || '—'}
                                </p>
                              </div>
                              <span className="shrink-0 text-xs text-gray-500 dark:text-gray-400">
                                <ClockIcon className="mr-1 inline h-3 w-3" />
                                {formatTime(trace.timestamp)}
                              </span>
                            </div>
                            <div className="mt-3 flex flex-wrap items-center gap-2">
                              <Badge variant="outline">{formatUsd(trace.total_cost_usd)}</Badge>
                              <Badge variant="outline">
                                {trace.latency_ms ? `${Math.round(trace.latency_ms)} ms` : '— ms'}
                              </Badge>
                              <Badge variant="outline">
                                {trace.total_tokens ? `${trace.total_tokens} tokens` : '— tokens'}
                              </Badge>
                            </div>
                            {trace.url && (
                              <a
                                href={trace.url}
                                target="_blank"
                                rel="noreferrer"
                                className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-eliza-red hover:underline"
                              >
                                Open trace
                                <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                              </a>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </PanelBody>
                </Panel>
              </div>
            </div>
          )}
        </PageBody>
      </Page>
    </Layout>
  );
}
